import React from 'react';
import { FileText, HelpCircle, Star, Clock, MapPin, Phone } from 'lucide-react';

const PatientInfo = () => {
  return (
    <div data-testid="patient-info-page">
      {/* Hero Section */}
      <section className="section-large bg-gradient-to-br from-blue-50 to-white" data-testid="patient-info-hero">
        <div className="container text-center">
          <h1 className="heading-primary" data-testid="patient-info-title">Patient Information</h1>
          <p className="text-large max-w-3xl mx-auto" data-testid="patient-info-subtitle">
            Everything you need to know for your visit, from preparation guidance to frequently asked questions.
          </p>
        </div>
      </section>

      {/* New Patient Guide */}
      <section className="section" data-testid="new-patient-guide">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <FileText className="text-blue-600 mx-auto mb-4" size={48} />
              <h2 className="heading-secondary" data-testid="new-patient-title">Guide for New Patients</h2>
              <p className="text-large" data-testid="new-patient-subtitle">
                Preparing for your first visit with Professor Vasha Kaur
              </p>
            </div>
            
            <div className="space-y-8">
              <div className="card" data-testid="before-visit">
                <h3 className="heading-tertiary mb-4">Before Your Visit</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">What to Bring:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                      <li>Valid photo identification</li>
                      <li>Insurance documents and any referral letters</li>
                      <li>A complete list of current medications</li>
                      <li>Recent test results, scans, or medical reports</li>
                      <li>Details of any previous surgery</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Preparation Guidance:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                      <li>Arrive 15 minutes early to complete paperwork</li>
                      <li>Wear comfortable, loose-fitting clothing</li>
                      <li>Prepare a list of questions or concerns</li>
                      <li>Consider bringing a family member or friend for support</li>
                      <li>Follow any specific pre-visit instructions provided</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="card" data-testid="during-visit">
                <h3 className="heading-tertiary mb-4">During Your Consultation</h3>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Your initial consultation is comprehensive and typically lasts around 30 minutes.
                    Professor Kaur will review your medical history, perform a focused examination,
                    and discuss your symptoms and goals in detail.
                  </p>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">What to Expect:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                      <li>Detailed medical history review</li>
                      <li>Physical examination relevant to your condition</li>
                      <li>Discussion of symptoms and treatment goals</li>
                      <li>Clear explanation of management options</li>
                      <li>Time for questions and next-step planning</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="card" data-testid="after-visit">
                <h3 className="heading-tertiary mb-4">After Your Visit</h3>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Following your consultation, you will receive a clear treatment plan and any
                    necessary follow-up instructions. Our team will coordinate further appointments
                    or investigations as required.
                  </p>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Next Steps:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                      <li>Review your personalised plan and timeline</li>
                      <li>Schedule any additional tests or procedures</li>
                      <li>Written instructions for at-home care</li>
                      <li>Direct contact details for questions or concerns</li>
                      <li>Follow-up appointment scheduling</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-gray-50" data-testid="faq-section">
        <div className="container">
          <div className="text-center mb-12">
            <HelpCircle className="text-blue-600 mx-auto mb-4" size={48} />
            <h2 className="heading-secondary" data-testid="faq-title">Frequently Asked Questions</h2>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="card" data-testid="faq-1">
              <h4 className="font-semibold text-gray-900 mb-2">How should I prepare for surgery?</h4>
              <p className="text-gray-600">
                Preparation varies depending on the procedure. You will receive tailored instructions,
                which may include dietary restrictions, medication adjustments, and arrival times.
                In general, avoid eating or drinking after midnight before your surgery unless advised otherwise.
              </p>
            </div>
            
            <div className="card" data-testid="faq-2">
              <h4 className="font-semibold text-gray-900 mb-2">What are the risks associated with surgery?</h4>
              <p className="text-gray-600">
                All procedures carry some risk. Specific risks related to your case will be discussed during your consultation.
                Professor Kaur employs meticulous surgical techniques to minimise any potential risks.
              </p>
            </div>
            
            <div className="card" data-testid="faq-3">
              <h4 className="font-semibold text-gray-900 mb-2">How long is the typical recovery time?</h4>
              <p className="text-gray-600">
                Recovery time varies by procedure and personal factors. Minimally invasive laparoscopic
                approaches usually allow faster recovery than open surgery. A tailored timeline will be
                provided following your consultation.
              </p>
            </div>
            
            <div className="card" data-testid="faq-4">
              <h4 className="font-semibold text-gray-900 mb-2">Do you work with my insurer?</h4>
              <p className="text-gray-600">
                We work with most major insurers. Please contact the office with your insurance details
                to confirm cover and discuss any potential out-of-pocket costs.
              </p>
            </div>
            
            <div className="card" data-testid="faq-5">
              <h4 className="font-semibold text-gray-900 mb-2">Can I seek a second opinion?</h4>
              <p className="text-gray-600">
                Absolutely. We support informed decision-making and will provide the necessary medical
                records should you wish to consult another specialist.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" data-testid="testimonials-section">
        <div className="container">
          <div className="text-center mb-12">
            <Star className="text-blue-600 mx-auto mb-4" size={48} />
            <h2 className="heading-secondary" data-testid="testimonials-title">Patient Testimonials</h2>
            <p className="text-large" data-testid="testimonials-subtitle">
              Hear from patients who have experienced exceptional care
            </p>
          </div>
          
          <div className="grid grid-2 gap-8 max-w-4xl mx-auto">
            <div className="card" data-testid="testimonial-1">
              <div className="mb-4">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="text-yellow-400 fill-current" size={20} />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">
                  "Professor Kaur performed my gallbladder surgery with exceptional skill and care.
                  The minimally invasive approach meant I was back to normal activities much sooner
                  than expected. Her bedside manner and clear explanations made the process reassuring."
                </p>
                <div>
                  <p className="font-semibold text-gray-900">Sarah M.</p>
                  <p className="text-sm text-gray-500">Gallbladder Surgery Patient</p>
                </div>
              </div>
            </div>
            
            <div className="card" data-testid="testimonial-2">
              <div className="mb-4">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="text-yellow-400 fill-current" size={20} />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">
                  "I felt genuinely listened to. The holistic, non-surgical weight assessment and
                  management plan gave me practical steps I could follow, and my overall health has improved significantly."
                </p>
                <div>
                  <p className="font-semibold text-gray-900">James R.</p>
                  <p className="text-sm text-gray-500">Non-Surgical Weight Management Patient</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section bg-gray-50" data-testid="contact-info-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-secondary" data-testid="contact-info-title">Questions? We&apos;re Here to Help</h2>
            <p className="text-large" data-testid="contact-info-subtitle">
              Contact our office for additional information or to schedule an appointment
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto grid grid-3 gap-8 text-center">
            <div data-testid="contact-phone">
              <Phone className="text-blue-600 mx-auto mb-2" size={24} />
              <p className="font-semibold">Phone</p>
              <p className="text-gray-600">07933196053</p>
            </div>
            
            <div data-testid="contact-location">
              <MapPin className="text-blue-600 mx-auto mb-2" size={24} />
              <p className="font-semibold">Location</p>
              <p className="text-gray-600">Kingston &amp; Richmond</p>
            </div>
            
            <div data-testid="contact-hours">
              <Clock className="text-blue-600 mx-auto mb-2" size={24} />
              <p className="font-semibold">Office Hours</p>
              <p className="text-gray-600">Mon–Fri: 9am–5pm</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientInfo;
